var Ajax = require('./ajax');
var Utils = require('./utils');
var URLMapper = require('./url-mapper');

/**
 * Storage API Client
 */
class StorageApiClient {
    /**
     * Constructor
     * @param {Object} data
     */
    constructor(data) {
        if (Utils.emptyObjectProperty(data, "applicationName")) {
            throw "StorageApiClient.configure: missing applicationName";
        }
        if (Utils.emptyObjectProperty(data, "applicationVersion")) {
            throw "StorageApiClient.configure: missing applicationVersion";
        }
        if (Utils.emptyObjectProperty(data, "storageApiUrl")) {
            throw "StorageApiClient.configure: missing storageApiUrl";
        }
        if (Utils.emptyObjectProperty(data, "sessionId") &&
            Utils.emptyObjectProperty(data, "authenticator")) {
            throw "StorageApiClient.configure: need either anonymous sessionId or an authenticator";
        }
        if (!Utils.emptyObjectProperty(data, "sessionId") && !data.sessionId
            .match(
                /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
            )) {
            throw "StorageApiClient.configure: sessionId must be a GUID";
        }
        if (Utils.emptyObjectProperty(data, "uploadIfDuplicate")) {
            throw "StorageApiClient.configure: missing uploadIfDuplicate";
        }
        if (Utils.emptyObjectProperty(data, "channel")) {
            throw "StorageApiClient.configure: missing channel";
        }
        if (Utils.emptyObjectProperty(data, "s3UrlMapperRegex")) {
            throw "StorageApiClient.configure: missing s3UrlMapperRegex";
        }

        this.serverUrl = data.storageApiUrl;

        // Authenticator is a function returning a promise of Bearer token
        // if the a legacy client passes a token, then it has the 'Bearer ' prefix which we don't need
        // in the authenticators mechanism
        this.authenticator = data.token ? (() => Promise.resolve(data.token.replace(/^Bearer /, ''))) : data.authenticator;

        this.uploadIfDuplicate = data.uploadIfDuplicate;
        this.returnDimensions = data.returnDimensions;
        this.applicationName = data.applicationName;
        this.applicationVersion = data.applicationVersion;
        this.sessionId = data.sessionId;
        this.channel = data.channel;
        this.ajaxRetryOptions = data.ajaxRetryOptions;
        this.s3UrlMapperRegex = data.s3UrlMapperRegex;
        this.authFailedEventListeners = data.authFailedEventListeners;
        this.canRecoverAuth = !!data.authenticator;
    }

    /**
     * Register file
     * @param file
     * @returns {Promise<any>}
     */
    registerFile(file) {
        const anonymousUpload = !this.authenticator
        const url = anonymousUpload
            ? `${this.serverUrl}/api/v1/anonymousuploads`
            : `${this.serverUrl}/api/v1/uploads`
        const defaultStorageType = anonymousUpload
            ? "AnonymousImageOriginal"
            : "ImageOriginal";

        const runRequest = (token) => Ajax.post({
            url,
            responseType: 'JSON',
            body: {
                localFileName: file.name,
                mimeType: file.type,
                fileSizeInBytes: file.size,
                base64MD5Hash: file.base64md5Checksum,
                uploadIfIsDuplicate: this.uploadIfDuplicate,
                applicationName: this.applicationName,
                applicationVersion: this.applicationVersion,
                sessionId: this.sessionId,
                channel: this.channel,
                storageFileType: file.storageFileType ? file.storageFileType : defaultStorageType
            },
            retry: this.ajaxRetryOptions,
            headers: {
                "Content-Type": "application/json",
                "Authorization": token ? `Bearer ${token}` : ''
            }
        })

        return this._withToken(runRequest)
            .then(response => {
                const json = response.data;
                const registrationId = json.id;
                if (!registrationId) {
                    return Promise.reject({ message: "StorageApiClient.registerImage() unable to parse json.", json })
                } else {
                    return { registrationId, uploadedAnonymously: anonymousUpload }
                }
            })
            .catch(wrapError)
    }

    /**
     * Get upload URL
     * @param {string} registrationId
     * @param {string} base64md5Checksum
     * @param {boolean} uploadedAnonymously
     * @returns {Promise<any>}
     */
    getUploadUrl(registrationId, base64md5Checksum, uploadedAnonymously) {
        const url = uploadedAnonymously
            ? `${this.serverUrl}/api/v1/anonymousuploads/${registrationId }/getUploadUrl`
            : `${this.serverUrl}/api/v1/uploads/${registrationId}/getUploadUrl`

        const runRequest = (token) => Ajax.post({
            url: url,
            responseType: 'JSON',
            body: {
                parts: [{ number: 1, contentMd5: base64md5Checksum }]
            },
            retry: this.ajaxRetryOptions,
            headers: {
                "Content-Type": "application/json",
                "Authorization": token ? `Bearer ${token}` : ''
            }
        })

        return this._withToken(runRequest)
            .then(response => {
                const json = response.data;
                if (!json || !json.hasOwnProperty("parts")) {
                    return Promise.reject({
                        message: "StorageApiClient.getUploadUrl: unable to parse json from server",
                        json: json,
                        url: url,
                        body: {
                            parts: [{ number: 1, contentMd5: base64md5Checksum }]
                        }
                    });
                }
                return { url: json.parts[0].url };
            })
            .catch(wrapError);
    }

    /**
     * Upload
     * @param {string} uploadUrl
     * @param file
     * @param {Function} onUploadProgressCallback
     * @param {string} base64MD5CheckSum
     * @param {Function} onUploadRetryCallback
     * @returns {{cancelToken: *, promise: Promise<any>}}
     */
    upload(uploadUrl, file, onUploadProgressCallback, base64MD5CheckSum, onUploadRetryCallback) {
        //The ajax request for uploading the file could take a long time, so we have a special cancelToken for that process
        const fileUploadAjaxToken = Ajax.getCancelToken();

        //The upload request
        const promise = Ajax.put({
                url: URLMapper.remapS3(uploadUrl, self.s3UrlMapperRegex),
                responseType: 'JSON',
                body: file,
                cancelToken: fileUploadAjaxToken.token,
                retry: this.ajaxRetryOptions,
                onUploadProgress: (data) => onUploadProgressCallback(file, data),
                onUploadRetry: (data) => onUploadRetryCallback(file, data),
                headers: {
                    "Content-Type": "application/octet-stream; charset=UTF-8",
                    "Content-MD5": base64MD5CheckSum
                },
            })
            .then(data => data.response.headers.etag); // WTF No wrapping here?

        return { promise, cancelToken: fileUploadAjaxToken }
    }

    /**
     * Complete upload
     * @param {string} registrationId
     * @param {string} etag
     * @param {boolean} uploadedAnonymously
     * @returns {Promise<any>}
     */
    completeUpload(registrationId, etag, uploadedAnonymously) {``
        const url = uploadedAnonymously
            ? `${this.serverUrl}/api/v1/anonymousuploads/${registrationId}/endUpload`
            : `${this.serverUrl}/api/v1/uploads/${registrationId}/endUpload`

        return this._withToken(token => Ajax.post({
                url,
                responseType: 'TEXT',
                body: { parts: [{ number: 1, eTag: etag }] },
                retry: this.ajaxRetryOptions,
                headers: {
                    'Content-type': 'application/json',
                    'Authorization': token ? `Bearer ${token}` : ''
                }
            }))
            .catch(wrapError)

    }

    /**
     * Get upload data
     * @param {Array} registrationIds
     * @param {boolean} uploadedAnonymously
     * @returns {Promise<any>}
     */
    getUploadData(registrationIds, uploadedAnonymously) {
        if (registrationIds.length === 0) {
            return Promise.resolve([])
        }

        const params = new URLSearchParams()
        params.set('ignoreMissingUploads', true)
        params.set('returnMD5Checksum', true)

        if (uploadedAnonymously) {
            params.set('sessionId', this.sessionId)
        }

        if (this.returnDimensions) {
            params.set('returnDimensions', true)
        }

        const url = uploadedAnonymously
            ? `${this.serverUrl}/api/v1/getanonymousuploads?${params.toString()}`
            : `${this.serverUrl}/api/v1/getuploads?${params.toString()}`

        return this._withToken(token => {
                const requests = Utils.chunk(registrationIds, 250)
                    .map(idsBatch => Ajax.post({
                        url,
                        responseType: 'TEXT',
                        body: idsBatch,
                        retry: this.ajaxRetryOptions,
                        headers: {
                            'Content-type': 'application/json',
                            'Authorization': token ? `Bearer ${token}` : ''
                        }
                    }))
                return Promise.all(requests)
            })
            .then(responses => responses.reduce((acc, {data}) => [...acc, ...data], [])) // flatten
            .catch(error => wrapError(error))
    }

    /**
     * File exists
     * @param {string} filename
     * @param {string} base64MD5CheckSum
     * @returns {Promise<{registrationId,uploadedAnonymously}|boolean>}
     */
    fileExists(filename, base64MD5CheckSum) {
        const anonymous = !this.authenticator
        const url = anonymous
            ? `${this.serverUrl}/api/v1/anonymousuploads/exists?sessionId=${this.sessionId}`
            : `${this.serverUrl}/api/v1/exists`

        return this._withToken(token => Ajax.post({
                url,
                responseType: 'JSON',
                body: [{ filename, base64MD5CheckSum }],
                retry: this.ajaxRetryOptions,
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": token ? `Bearer ${token}` : ''
                }
            }))
            .then(response => {
                const json = response.data;
                if (json[0].exists) {
                    return { registrationId: json[0].uploadId, uploadedAnonymously: anonymous }
                } else {
                    return false;
                }

            }).catch(wrapError);
    }

    /**
     * Link all anonymous photos by session id.
     */
    linkAllAnonymousPhotosBySessionId() {
        if (!this.sessionId) {
            return Promise.reject("No session ID provided");
        }
        if (!this.authenticator) {
            return Promise.reject("Authenticator not provided");
        }
        if (!this.channel) {
            return Promise.reject("Channel not provided");
        }

        return this._withToken(token => Ajax.post({
                url: `${this.serverUrl}/api/v1/linksessiontouser?channel=${this.channel}&sessionId=${this.sessionId}`,
                dataType: "json",
                responseType: 'NONE',
                retry: this.ajaxRetryOptions,
                body: [],
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": token ? `Bearer ${token}` : ''
                }
            }))
            .then(() => { this.sessionId = null; return null; })
            .catch(wrapError)
    }

    /**
     * Link uploads to user
     * @param {Array} registrationIdList
     * @returns {Promise<any>}
     */
    linkUploadsToUser(registrationIdList) {
        if (this.channel === "" || !this.authenticator) {
            return Promise.resolve([]);
        }

        return this._withToken(token => {
                const requests = Utils.chunk(registrationIdList, 250)
                    .map(idsBatch => Ajax.post({
                        url: `${this.serverUrl}/api/v1/linktouser?channel=${this.channel}`,
                        responseType: 'JSON',
                        retry: this.ajaxRetryOptions,
                        body: idsBatch,
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": token ? `Bearer ${token}` : ''
                        }
                    }));

                return Promise.all(requests).then(() => registrationIdList)
            })
            .catch(wrapError)
    }

    /**
     * Update authorization token
     * @param token
     * @deprecated Use the authenticator mechanism instead
     */
    updateToken(token) {
        this.authenticator = () => Promise.resolve(token.replace(/^Bearer /, ''));
    }

    updateAuthenticator(authenticator) {
        this.authenticator = authenticator
    }

    /**
     * Run a promise computation with token
     * If a token is invalid (HTTP 401) then an error is fired, the
     * designated authenticator is queried again and the promise function
     * is restarted.
     *
     * @param {string => Promise<any>} promiseFunc
     */
    _withToken(promiseFunc) {
        if (!this.authenticator) {
            return promiseFunc("")
        }

        // Overcoming the lack of tail call optimization...
        let handleAuthErrors;
        const runWithToken = () => this.authenticator()
                    .then(token => promiseFunc(token)
                        .catch(err => handleAuthErrors(token, err)))


        handleAuthErrors = (token, error) => {
            if (!this.canRecoverAuth) {
                return Promise.reject(error);
            }
            if (error.error && error.status === 401) {
                this.emitAuthErrorEvent(token)
                return runWithToken()
            } else {
                return Promise.reject(error)
            }
        };

        return runWithToken()
    }

    emitAuthErrorEvent(error) {
        if (this.authFailedEventListeners && this.authFailedEventListeners.length) {
            this.authFailedEventListeners.forEach(callback => callback(error))
        }
    }
}

// FIXME Unnecessary nesting of error objects
// Requires updating all clients, that's why we don't do it for now
const wrapError = (error) => {
    return error.error
        ? Promise.reject({error})
        : error;
}

module.exports = StorageApiClient;
